import {
  AppRootElementBlueprint,
  createFrontendPlugin,
  PageBlueprint,
  ApiBlueprint,
  discoveryApiRef,
  fetchApiRef,
  errorApiRef,
  identityApiRef,
  PluginHeaderActionBlueprint,
} from '@backstage/frontend-plugin-api';
import { UpdatesIcon } from '@xlrt/components';
import { NewAnnouncementBanner } from './components/NewAnnouncementBanner';
import { rootRouteRef } from './routes';
import { SearchResultListItemBlueprint } from '@backstage/plugin-search-react/alpha';
import { EntityContentBlueprint } from '@backstage/plugin-catalog-react/alpha';
import { announcementsApiRef, AnnouncementsClient } from './apis';

const announcementsApiFactory = ApiBlueprint.make({
  params: defineParams =>
    defineParams({
      api: announcementsApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        identityApi: identityApiRef,
        errorApi: errorApiRef,
        fetchApi: fetchApiRef,
      },
      factory: ({ discoveryApi, identityApi, errorApi, fetchApi }) => {
        return new AnnouncementsClient({
          discoveryApi: discoveryApi,
          identityApi: identityApi,
          errorApi: errorApi,
          fetchApi: fetchApi,
        });
      },
    }),
});

export const AnnouncementsPage = PageBlueprint.make({
  name: 'Announcements',
  params: {
    path: '/announcements',
    title: 'Updates',
    icon: <UpdatesIcon />,
    routeRef: rootRouteRef,
    loader: () =>
      import('./components/Router').then(m => (
        <m.Router
          hideContextMenu
          buttonOptions={{ name: 'Update' }}
          title="Updates"
        />
      )),
  },
});

/** "New Update" and "Categories" in the header of the update list. */
export const announcementsHeaderActions = PluginHeaderActionBlueprint.make({
  name: 'announcements-actions',
  params: {
    loader: () =>
      import('./components/AnnouncementsPage/HeaderActions/AnnouncementsHeaderActions').then(
        m => <m.AnnouncementsHeaderActions />,
      ),
  },
});

/**
 * Adding the Search Result List Item using the Search blueprint
 */
const AnnouncementSearchResultItem = SearchResultListItemBlueprint.make({
  params: {
    predicate: result => result.type === 'announcements',
    component: () =>
      import('./components/AnnouncementSearchResultListItem').then(
        m => m.AnnouncementSearchResultListItem,
      ),
  },
});

/**
 * Floating banner for announcements the user has not seen yet, shown on every
 * page below the global header.
 */
const newAnnouncementBanner = AppRootElementBlueprint.make({
  name: 'new-announcement-banner',
  params: {
    element: <NewAnnouncementBanner active variant="floating" />,
  },
});

/** The Updates tab of groups: the announcements of the group. */
export const groupUpdatesEntityContent = EntityContentBlueprint.make({
  name: 'group-updates',
  params: {
    path: '/updates',
    title: 'Updates',
    filter: { kind: 'group' },
    loader: () =>
      import('./entity-content/GroupUpdatesTab').then(m => (
        <m.GroupUpdatesTab />
      )),
  },
});

export const announcementsTabPlugin = createFrontendPlugin({
  // The plugin ID is now provided as `pluginId` instead of `id`
  pluginId: 'announcements',
  // bind all the extensions to the plugin
  extensions: [
    announcementsApiFactory,
    AnnouncementsPage,
    announcementsHeaderActions,
    AnnouncementSearchResultItem,
    newAnnouncementBanner,
    groupUpdatesEntityContent,
  ],
  routes: {
    root: rootRouteRef,
  },
});
